import React, { createContext, useContext, useState, useMemo, useCallback, useEffect } from 'react'
import { bankData } from '../data/bankData'
import { translations } from '../data/translations'

// 搜索工具函數
const escapeRegExp = (string) => {
  return string.replace(/[.*+?^${}()|[\\]\\$]/g, '\\$&')
}

const searchBanks = (banks, query) => {
  if (!query) return banks
  
  const escapedQuery = escapeRegExp(query.toLowerCase())
  const regex = new RegExp(escapedQuery, 'i')
  
  return banks.filter(bank => 
    regex.test(bank.code) || 
    regex.test(bank.nameEn.toLowerCase()) || 
    regex.test(bank.nameZh.toLowerCase())
  )
}

const sortBanks = (banks, field, direction) => {
  return [...banks].sort((a, b) => {
    let aValue = a[field]
    let bValue = b[field]
    
    // 特殊處理代碼排序（按數值而非字符串）
    if (field === 'code') {
      aValue = parseInt(aValue, 10)
      bValue = parseInt(bValue, 10)
    } else {
      aValue = aValue.toLowerCase()
      bValue = bValue.toLowerCase()
    }
    
    if (aValue < bValue) return direction === 'asc' ? -1 : 1
    if (aValue > bValue) return direction === 'asc' ? 1 : -1
    return 0
  })
}

// 台灣銀行分類篩選函數
const filterByTaiwanCategory = (banks, categoryId, viewMode = 'category') => {
  if (!categoryId || categoryId === 'all') return banks
  
  if (viewMode === 'category') {
    // 按機構類型分類
    const categoryMappings = {
      'central': ['001'],
      'public': ['004', '005', '006', '007', '008', '009', '017', '050'],
      'private': ['011', '012', '013', '016', '048', '053', '054', '101', '102', '103', '108', '147', '803', '805', '806', '807', '808', '809', '812', '814', '815', '816', '822'],
      'foreign': ['015', '021', '039', '052', '072', '075', '076', '081', '082', '085', '097', '810'],
      'digital': ['823', '824', '825'],
      'credit-coop': [], // 使用範圍篩選 114-224
      'farmers': [], // 使用範圍篩選 251-684
      'postal': ['700'],
      'fishermen': [], // 使用範圍篩選 951-971
      'securities': ['161', '162', '163', '164', '901', '902', '903', '904', '905'],
      'specialized': ['018', '512']
    }
    
    const categoryCodes = categoryMappings[categoryId]
    
    if (categoryCodes && categoryCodes.length > 0) {
      return banks.filter(bank => categoryCodes.includes(bank.code))
    }
    
    // 處理範圍篩選
    if (categoryId === 'credit-coop') {
      return banks.filter(bank => {
        const code = parseInt(bank.code, 10)
        return code >= 114 && code <= 224
      })
    }
    
    if (categoryId === 'farmers') {
      return banks.filter(bank => {
        const code = parseInt(bank.code, 10)
        return code >= 251 && code <= 684
      })
    }
    
    if (categoryId === 'fishermen') {
      return banks.filter(bank => {
        const code = parseInt(bank.code, 10)
        return code >= 951 && code <= 971
      })
    }
  } else {
    // 按代碼範圍分類
    const codeRangeMappings = {
      '001-099': [1, 99],
      '100-199': [100, 199],
      '200-299': [200, 299],
      '300-499': [300, 499],
      '500-699': [500, 699],
      '700-799': [700, 799],
      '800-899': [800, 899],
      '900-999': [900, 999]
    }
    
    const range = codeRangeMappings[categoryId]
    if (range) {
      const [start, end] = range
      return banks.filter(bank => {
        const code = parseInt(bank.code, 10)
        return code >= start && code <= end
      })
    }
  }
  
  return banks
}

const AppContext = createContext()

export const useApp = () => {
  const context = useContext(AppContext)
  if (!context) {
    throw new Error('useApp must be used within an AppProvider')
  }
  return context
}

export const AppProvider = ({ children }) => {
  const [currentLang, setCurrentLang] = useState('zh')
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // 檢查本地存儲的主題設置
    const savedTheme = localStorage.getItem('theme')
    if (savedTheme) {
      return savedTheme === 'dark'
    }
    // 如果沒有保存的設置，檢查系統偏好
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
  })
  const [searchQuery, setSearchQuery] = useState('')
  const [sortField, setSortField] = useState('code')
  const [sortDirection, setSortDirection] = useState('asc')
  const [activeRange, setActiveRange] = useState('all')
  const [viewMode, setViewMode] = useState('category') // 'category' or 'codeRange'
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(20)
  
  // 獲取當前時間作為最後更新時間
  const lastUpdated = useMemo(() => new Date().toLocaleDateString(currentLang === 'zh' ? 'zh-TW' : 'en-US'), [currentLang])

  // 應用黑暗模式到 DOM
  useEffect(() => {
    const root = document.documentElement
    if (isDarkMode) {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }
    // 保存到本地存儲
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light')
  }, [isDarkMode])

  // 切換語言
  const toggleLanguage = useCallback(() => {
    setCurrentLang(prev => prev === 'en' ? 'zh' : 'en')
  }, [])

  // 切換主題
  const toggleTheme = useCallback(() => {
    setIsDarkMode(prev => !prev)
  }, [])

  // 處理排序
  const handleSort = useCallback((field) => {
    setSortField(prevField => {
      if (prevField === field) {
        setSortDirection(prevDirection => prevDirection === 'asc' ? 'desc' : 'asc')
        return field
      } else {
        setSortDirection('asc')
        return field
      }
    })
  }, [])

  // 設置快速導航範圍（現在處理台灣銀行分類和代碼範圍）
  const setQuickNavRange = useCallback((category, mode = 'category') => {
    setActiveRange(category.id)
    setViewMode(mode)
    setCurrentPage(1) // 重置到第一頁
  }, [])

  // 重置搜索
  const clearSearch = useCallback(() => {
    setSearchQuery('')
    setCurrentPage(1)
  }, [])

  // 處理頁碼變更
  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage)
  }, [])

  // 處理每頁項目數變更 - 限制最大值為50
  const handleItemsPerPageChange = useCallback((newItemsPerPage) => {
    // 確保不超過50項，並確保是有效數字
    const safeItemsPerPage = Math.min(Math.max(newItemsPerPage, 10), 50)
    setItemsPerPage(safeItemsPerPage)
    setCurrentPage(1) // 重置到第一頁
  }, [])

  // 原始銀行數據（未經篩選/搜索）
  const originalTotalCount = useMemo(() => bankData.length, [])

  // 搜索後的銀行數據
  const searchedBanks = useMemo(() => {
    return searchBanks(bankData, searchQuery)
  }, [searchQuery])

  // 排序後的銀行數據
  const sortedBanks = useMemo(() => {
    return sortBanks(searchedBanks, sortField, sortDirection)
  }, [searchedBanks, sortField, sortDirection])

  // 按台灣銀行分類過濾的銀行數據
  const categoryFilteredBanks = useMemo(() => {
    return filterByTaiwanCategory(sortedBanks, activeRange, viewMode)
  }, [sortedBanks, activeRange, viewMode])

  // 總數（用於分頁計算）
  const totalCount = useMemo(() => {
    return categoryFilteredBanks.length
  }, [categoryFilteredBanks])

  // 計算總頁數
  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(totalCount / itemsPerPage))
  }, [totalCount, itemsPerPage])

  // 確保當前頁面在有效範圍內
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, totalPages])

  // 分頁後的銀行數據
  const paginatedBanks = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return categoryFilteredBanks.slice(startIndex, endIndex)
  }, [categoryFilteredBanks, currentPage, itemsPerPage])

  // 計算顯示範圍
  const startIndex = useMemo(() => {
    if (totalCount === 0) return 0
    return (currentPage - 1) * itemsPerPage + 1
  }, [currentPage, itemsPerPage, totalCount])

  const endIndex = useMemo(() => {
    if (totalCount === 0) return 0
    return Math.min(currentPage * itemsPerPage, totalCount)
  }, [currentPage, itemsPerPage, totalCount])

  // 分頁導航功能
  const goToPage = useCallback((page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
    }
  }, [totalPages])

  const goToPreviousPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1)
    }
  }, [currentPage])

  const goToNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1)
    }
  }, [currentPage, totalPages])

  const canGoPrevious = useMemo(() => currentPage > 1, [currentPage])
  const canGoNext = useMemo(() => currentPage < totalPages, [currentPage, totalPages])

  // 判斷是否應該顯示分頁
  const shouldShowPagination = useMemo(() => totalCount > itemsPerPage, [totalCount, itemsPerPage])

  const contextValue = {
    // 狀態
    currentLang,
    isDarkMode,
    searchQuery,
    sortField,
    sortDirection,
    activeRange,
    viewMode,
    currentPage,
    itemsPerPage,
    lastUpdated,
    totalCount,
    originalTotalCount,
    totalPages,
    startIndex,
    endIndex,
    shouldShowPagination,
    
    // 分頁狀態
    canGoPrevious,
    canGoNext,
    
    // 數據
    paginatedBanks,
    bankData, // 提供給 QuickNavigation 使用
    translations,
    
    // 方法
    toggleLanguage,
    toggleTheme,
    handleSort,
    setQuickNavRange,
    setSearchQuery,
    clearSearch,
    handlePageChange: goToPage,
    handleItemsPerPageChange,
    
    // 分頁方法
    goToPage,
    goToPreviousPage,
    goToNextPage
  }

  // 調試信息（開發時使用）
  useEffect(() => {
    console.log('🔍 Pagination Debug Info:', {
      totalCount,
      currentPage,
      itemsPerPage,
      totalPages,
      startIndex,
      endIndex,
      paginatedBanksLength: paginatedBanks.length,
      shouldShowPagination
    })
  }, [totalCount, currentPage, itemsPerPage, totalPages, startIndex, endIndex, paginatedBanks.length, shouldShowPagination])

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  )
}